// filepath: apps/web/src/features/admin/recordPermissions/EntityFieldOwnerDialog.tsx
//
// FR-UI-ADM-2 AC-1 — entity-field-owner selector. Picks which field
// of an entity type acts as the "owner" (referenced by record-level
// ownership rules).
//
// INV-7: If-Match carries the role's current version; on 409 the
// server returns current_version + server_owner_field, surfaced
// via RecordPermissionConflictDialog.

import React, { useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  useEntityFieldOwnerMutation,
  useEntityFieldOwnerQuery,
} from "./queries";

interface EntityFieldOwnerDialogProps {
  open: boolean;
  entityType: string;
  fields: string[];
  onClose: () => void;
  onSaved: () => void;
  onConflict: (serverVersion: number, serverOwnerField: string | null) => void;
}

export function EntityFieldOwnerDialog({
  open,
  entityType,
  fields,
  onClose,
  onSaved,
  onConflict,
}: EntityFieldOwnerDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const query = useEntityFieldOwnerQuery(entityType);
  const mutation = useEntityFieldOwnerMutation(entityType);

  const [ownerField, setOwnerField] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(1);

  useEffect(() => {
    if (open && query.data) {
      setOwnerField(query.data.owner_field ?? null);
      setVersion(query.data.version);
    }
  }, [open, query.data]);

  if (!open) return null;

  const onSave = async () => {
    if (ownerField === null || ownerField.trim() === "") return;
    try {
      await mutation.mutateAsync({ owner_field: ownerField, version });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          const body = JSON.parse(err.message) as {
            current_version?: number;
            server_owner_field?: string | null;
          };
          if (body.current_version !== undefined) {
            onConflict(body.current_version, body.server_owner_field ?? null);
          }
        } catch {
          // swallow
        }
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="entity-field-owner-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="entity-field-owner-dialog"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="entity-field-owner-title" className="text-sm font-semibold">
          {t("admin.entity_field_owner.title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{entityType}</p>
        <div className="mt-3">
          <label className="text-xs">
            {t("admin.entity_field_owner.field_label")}
          </label>
          <select
            className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-xs"
            value={ownerField ?? ""}
            onChange={(e) => setOwnerField(e.target.value)}
            data-testid="entity-field-owner-select"
          >
            <option value="">--</option>
            {fields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="entity-field-owner-close"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
            onClick={onSave}
            disabled={
              mutation.isPending ||
              ownerField === null ||
              ownerField.trim() === ""
            }
            data-testid="entity-field-owner-save"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
